import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { Icon } from '../../../../layout/Icon.jsx';

const TYPES = [
  { key: 'percent', label: 'نسبة %' },
  { key: 'amount', label: 'مبلغ' }
];

const SCOPES = [
  { key: 'sibling', label: 'الأشقاء' },
  { key: 'school', label: 'مدرسي' },
  { key: 'vip', label: 'VIP' },
  { key: 'manual', label: 'يدوي' }
];

function defaultsFrom(d) {
  return {
    name: d?.name || '',
    type: d?.type || 'percent',
    value: d?.value ?? 10,
    // الافتراضي "يدوي" حتى يظهر الخصم في قائمة التسوية. "الأشقاء" يُطبَّق تلقائياً
    // حسب عدد الإخوة فلا يُختار يدوياً، لذا لا يكون افتراضاً.
    scope: d?.scope || 'manual',
    minQty: d?.minQty ?? 2,
    isActive: d?.isActive !== false
  };
}

export function DiscountForm({ open, discount, onClose, onSubmit, submitting }) {
  const isEdit = !!discount;
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors }
  } = useForm({ defaultValues: defaultsFrom(discount) });

  useEffect(() => {
    if (open) reset(defaultsFrom(discount));
  }, [open, discount, reset]);

  const type = watch('type');
  const scope = watch('scope');

  if (!open) return null;

  const submit = (values) => {
    if (!values.name || !values.name.trim()) return;
    const body = {
      name: values.name.trim(),
      type: values.type,
      value: Number(values.value) || 0,
      scope: values.scope,
      minQty: Number(values.minQty) || 0,
      isActive: !!values.isActive
    };
    if (body.type === 'percent') {
      body.value = Math.max(0, Math.min(100, body.value));
    }
    onSubmit?.(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="card w-[520px] max-w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">
            {isEdit ? 'تعديل خصم' : 'خصم جديد'}
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم *</label>
            <input
              className={clsx('input', errors.name && 'border-red')}
              placeholder="مثلاً: خصم الأشقاء"
              {...register('name', { required: true })}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">النوع</label>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
                  {TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => field.onChange(t.key)}
                      className={clsx(
                        'flex-1 px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                        field.value === t.key
                          ? 'bg-orange text-white shadow-orange-glow'
                          : 'text-ink-2 hover:bg-white'
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">
              {type === 'percent' ? 'القيمة (%)' : 'القيمة (د.ع)'}
            </label>
            <input
              className="input"
              type="number"
              min="0"
              max={type === 'percent' ? 100 : undefined}
              {...register('value', { valueAsNumber: true })}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">النطاق</label>
            <Controller
              control={control}
              name="scope"
              render={({ field }) => (
                <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full flex-wrap">
                  {SCOPES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => field.onChange(s.key)}
                      className={clsx(
                        'flex-1 min-w-[80px] px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                        field.value === s.key
                          ? 'bg-orange text-white shadow-orange-glow'
                          : 'text-ink-2 hover:bg-white'
                      )}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          {scope === 'sibling' ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">
                يبدأ التطبيق من الطفل رقم (الحد الأدنى للكمية)
              </label>
              <input
                className="input"
                type="number"
                min="1"
                {...register('minQty', { valueAsNumber: true })}
              />
              <div className="text-[11px] text-ink-3 mt-1">
                مثلاً 2 يعني يُطبَّق الخصم بدءاً من الطفل الثاني فما فوق.
              </div>
            </div>
          ) : null}

          <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
            <input type="checkbox" className="accent-orange" {...register('isActive')} />
            فعّال
          </label>
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ' : 'إنشاء'}
          </button>
        </div>
      </form>
    </div>
  );
}
